import type { PipelineTile } from "../api";
import { ragTone } from "../tone";
import { MEASURES, measureChipClass, measureRag, tileNote, tileToneClass, toneDotClass } from "./measure";

/**
 * One compact stage tile (UX-mockup prototype v2, frame 1 `.st`). Overall
 * tone comes straight from `overall_rag` through the shared `ragTone` map
 * (dev spec I-4) — this component never computes a colour of its own, and
 * never prints the rag word: a small tone dot plus four Q/T/F/R letter
 * chips (each coloured by its own measure rag) carry the signal instead.
 *
 * Opening the tile is reading, not editing (`data-nonmutating`): PROTEX's
 * `NeedsAttentionStrip` sets the same precedent for a filter/selection
 * control under `PermissionGate`.
 */
export function StepTile({
  tile,
  onOpen,
  noun = "week",
}: {
  tile: PipelineTile;
  onOpen: (stageKey: string) => void;
  noun?: "week" | "month";
}) {
  const tone = ragTone(tile.overall_rag);
  const note = tileNote(tile, noun);

  return (
    <button
      type="button"
      data-nonmutating
      data-tile={tile.stage_key}
      data-tone={tone}
      onClick={() => onOpen(tile.stage_key)}
      className={`flex min-w-24 flex-col gap-1 rounded-ks-sm border bg-ks-surface px-2 py-1.5 text-left ${tileToneClass(tone)}`}
    >
      <span className="flex items-center gap-1.5">
        <span className="text-sm font-extrabold leading-none text-ks-purple">{tile.step ?? tile.stage_key}</span>
        <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${toneDotClass(tone)}`} />
      </span>
      <span className="text-xs font-semibold leading-tight text-ks-ink">{tile.label}</span>
      <span className="flex gap-1" aria-hidden>
        {MEASURES.map((m) => (
          <span
            key={m.key}
            title={m.name}
            className={`flex h-4 w-4 items-center justify-center rounded text-[10px] font-bold ${measureChipClass(measureRag(tile, m.key))}`}
          >
            {m.letter}
          </span>
        ))}
      </span>
      {note && <span className="text-[10px] leading-tight text-ks-grey-1">{note}</span>}
      {tile.low_sample && (
        <span data-low-sample className="text-[10px] leading-tight text-ks-grey-1">
          Low sample (n={tile.n})
        </span>
      )}
    </button>
  );
}
